import React, { useState, useRef } from 'react';
import {
    HStack,
    Text,
    Avatar,
    Box,
    Input,
    useToast,
    Icon,
} from '@chakra-ui/react';
import { FiEdit2, FiCheck, FiX, FiCamera } from 'react-icons/fi';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';

const ProfileHeader = ({ walletAddress }) => {
    const toast = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [displayName, setDisplayName] = useState('My Profile');
    const [profileImage, setProfileImage] = useState(null);
    const fileInputRef = useRef();

    const handleNameSubmit = () => {
        if (displayName.trim()) {
            setIsEditing(false);
            toast({
                title: 'Profile Updated',
                description: 'Your display name has been updated',
                status: 'success',
                duration: 2000,
            });
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
                toast({
                    title: 'Profile Picture Updated',
                    status: 'success',
                    duration: 2000,
                });
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <HStack justify="space-between" mb={6} align="center">
            <HStack spacing={4}>
                <Box 
                    position="relative" 
                    role="group"
                    width="fit-content"
                >
                    <Avatar
                        size="md"
                        src={profileImage}
                        bg="purple.400"
                        cursor="pointer"
                        onClick={() => fileInputRef.current?.click()}
                    />
                    <Box
                        position="absolute"
                        bottom="0"
                        right="0"
                        bg="purple.400"
                        borderRadius="full"
                        width="16px"
                        height="16px"
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        _groupHover={{ 
                            display: "flex"
                        }}
                        transition="all 0.2s"
                        border="1px solid"
                        borderColor="gray.800"
                        cursor="pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                        }}
                    >
                        <Icon as={FiCamera} boxSize={2} color="white" />
                    </Box>
                    <Input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        display="none"
                        onChange={handleImageChange}
                    />
                </Box>

                {isEditing ? (
                    <HStack>
                        <Input
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            size="md"
                            variant="filled"
                            bg="whiteAlpha.100"
                            color="white"
                            maxW="200px"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            _focus={{ bg: 'whiteAlpha.200' }}
                        />
                        <Icon as={FiCheck} boxSize={5} color="green.400" onClick={handleNameSubmit} aria-label="Save name" />
                        <Icon as={FiX} boxSize={5} color="red.400" onClick={() => setIsEditing(false)} aria-label="Cancel" />
                    </HStack>
                ) : (
                    <HStack>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="white">
                            {displayName}
                        </Text>
                        <Icon as={FiEdit2} boxSize={5} color="purple.400" onClick={() => setIsEditing(true)} aria-label="Edit name" />
                    </HStack>
                )}
            </HStack>

            <Box
                className="wallet-button-container"
                sx={{
                    '.wallet-adapter-button': {
                        bg: 'transparent',
                        border: '2px solid',
                        borderColor: '#8247E5',
                        color: '#8247E5',
                        borderRadius: 'full',
                        fontSize: 'md',
                        fontWeight: 'medium',
                        height: '40px',
                        padding: '0 24px',
                        _hover: {
                            bg: '#8247E5',
                            color: 'black',
                        },
                        transition: 'all 0.2s',
                        cursor: 'pointer',
                    },
                    '.wallet-adapter-dropdown': {
                        position: 'relative',
                        zIndex: 1000,
                    },
                    '.wallet-adapter-dropdown-list': {
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 8px)',
                        width: 'auto',
                        minWidth: '220px',
                        background: 'rgba(20, 20, 20, 0.95)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.6)',
                        padding: '10px',
                        zIndex: 1000,
                    },
                    '.wallet-adapter-dropdown-list-item': {
                        padding: '12px 15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'medium',
                        marginBottom: '4px',
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        _hover: {
                            background: 'rgba(130, 71, 229, 0.3)',
                        },
                        _active: {
                            background: 'rgba(130, 71, 229, 0.5)',
                        },
                        '&:last-child': {
                            marginBottom: 0
                        }
                    }
                }}
                position="relative"
            >
                <WalletMultiButton />
            </Box>
        </HStack>
    );
};

export default ProfileHeader;
